import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef } from 'react';
import { formatLength, degreesToPitch, stepControl, } from '../../util/units';
export function DimensionAccordion({ controls, onSave, btnText = '->', units = 'm', onUnitsChange, }) {
    const [activeIndex, setActiveIndex] = useState(0);
    const touchStartX = useRef(null);
    const prev = () => setActiveIndex(i => (i - 1 + controls.length) % controls.length);
    const next = () => setActiveIndex(i => (i + 1) % controls.length);
    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (touchStartX.current === null)
            return;
        const dx = e.changedTouches[0].clientX - touchStartX.current;
        if (dx > 50)
            prev();
        else if (dx < -50)
            next();
        touchStartX.current = null;
    };
    const active = controls[activeIndex];
    const displayValue = active.isAngle && units === 'ft'
        ? degreesToPitch(active.value)
        : formatLength(active.value, units);
    const atMin = active.min !== undefined && active.value <= active.min;
    const atMax = active.max !== undefined && active.value >= active.max;
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: styles }), _jsxs("div", { className: "dim-accordion", onTouchStart: onTouchStart, onTouchEnd: onTouchEnd, children: [onUnitsChange && (_jsxs("div", { className: "dim-accordion__unit-toggle", children: [_jsx("button", { className: `dim-accordion__unit-btn ${units === 'm' ? 'dim-accordion__unit-btn--active' : ''}`, onClick: () => onUnitsChange('m'), children: "M" }), _jsx("button", { className: `dim-accordion__unit-btn ${units === 'ft' ? 'dim-accordion__unit-btn--active' : ''}`, onClick: () => onUnitsChange('ft'), children: "FT" })] })), _jsx("div", { className: "dim-accordion__dots", children: controls.map((c, i) => (_jsx("button", { className: `dim-accordion__dot ${i === activeIndex ? 'dim-accordion__dot--active' : ''}`, onClick: () => setActiveIndex(i), title: c.label }, i))) }), _jsx("div", { className: "dim-accordion__label", children: active.label }), _jsxs("div", { className: "dim-accordion__row", children: [_jsx("button", { className: "dim-accordion__nav", onClick: prev, children: "\u2039" }), _jsxs("div", { className: "dim-accordion__control", children: [_jsx("button", { className: "dim-accordion__btn", onClick: () => active.onChange(stepControl(active, -1)), disabled: atMin, children: "\u2212" }), _jsx("div", { className: "dim-accordion__value", children: displayValue }), _jsx("button", { className: "dim-accordion__btn", onClick: () => active.onChange(stepControl(active, 1)), disabled: atMax, children: "+" })] }), _jsx("button", { className: "dim-accordion__nav", onClick: next, children: "\u203A" })] }), _jsx("div", { className: "dim-accordion__progress", children: _jsx("div", { className: "dim-accordion__progress-fill", style: { width: `${((activeIndex + 1) / controls.length) * 100}%` } }) }), _jsxs("div", { className: "dim-accordion__counter", children: [activeIndex + 1, " / ", controls.length] }), onSave && (_jsx("button", { className: "dim-accordion__save", onClick: onSave, children: btnText }))] })] }));
}
const styles = `
.dim-accordion {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    background: rgb(37 73 136 / 92%);
    padding: 14px 16px 12px;
    width: 100%;
    box-sizing: border-box;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
}
.dim-accordion__unit-toggle {
    display: flex;
    background: rgba(255,255,255,0.1);
    border-radius: 8px;
    padding: 3px;
    gap: 3px;
    align-self: flex-end;
}
.dim-accordion__unit-btn {
    height: 24px;
    padding: 0 12px;
    border: none;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    background: transparent;
    color: rgba(255,255,255,0.4);
    -webkit-tap-highlight-color: transparent;
}
.dim-accordion__unit-btn--active {
    background: rgba(255,255,255,0.2);
    color: #fff;
}
.dim-accordion__dots {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: center;
}
.dim-accordion__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(255,255,255,0.3);
    border: none;
    cursor: pointer;
    padding: 0;
    transition: background 0.2s, transform 0.2s;
}
.dim-accordion__dot--active {
    background: #ffffff;
    transform: scale(1.3);
}
.dim-accordion__label {
    color: #fff;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    opacity: 0.8;
    min-height: 16px;
}
.dim-accordion__row {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    justify-content: center;
}
.dim-accordion__nav {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.25);
    color: #fff;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s;
    -webkit-tap-highlight-color: transparent;
}
.dim-accordion__nav:active { background: rgba(255,255,255,0.28); }
.dim-accordion__control {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    max-width: 220px;
    justify-content: center;
}
.dim-accordion__btn {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(255,255,255,0.15);
    border: 1px solid rgba(255,255,255,0.3);
    color: #fff;
    font-size: 22px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s, transform 0.1s;
    -webkit-tap-highlight-color: transparent;
}
.dim-accordion__btn:active { background: rgba(255,255,255,0.35); transform: scale(0.93); }
.dim-accordion__btn:disabled { opacity: 0.25; transform: none; }
.dim-accordion__value {
    min-width: 80px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    border: 1px solid rgba(255,255,255,0.4);
    background: rgba(255,255,255,0.1);
    color: #fff;
    font-size: 18px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    padding: 0 8px;
}
.dim-accordion__progress {
    width: 100%;
    height: 3px;
    background: rgba(255,255,255,0.2);
    border-radius: 2px;
    overflow: hidden;
}
.dim-accordion__progress-fill {
    height: 100%;
    background: #fff;
    border-radius: 2px;
    transition: width 0.25s ease;
}
.dim-accordion__counter {
    font-size: 11px;
    color: rgba(255,255,255,0.5);
}
.dim-accordion__save {
    margin-top: 4px;
    width: 100%;
    height: 44px;
    border-radius: 10px;
    background: #fff;
    color: rgb(37 73 136);
    border: none;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    letter-spacing: 0.04em;
    transition: opacity 0.15s;
    -webkit-tap-highlight-color: transparent;
}
.dim-accordion__save:active { opacity: 0.8; }
`;
